/**
 * Cache Service for API request caching
 * Implements both memory and localStorage caching with TTL support
 */

class CacheService {
  constructor() {
    this.memoryCache = new Map();
    this.maxMemoryCacheSize = 50; // Maximum items in memory cache
    this.defaultTTL = 5 * 60 * 1000; // 5 minutes default TTL
    this.localStorageKey = 'bmb_api_cache';
    this.stats = {
      hits: 0,
      misses: 0,
      sets: 0
    };
    
    // Clean up expired entries on initialization
    this.clearExpired();
    
    // Set up periodic cleanup (every 5 minutes)
    setInterval(() => this.clearExpired(), 5 * 60 * 1000);
  }

  /**
   * Generate a cache key from URL and parameters
   */
  generateKey(url, params = {}) {
    const sortedParams = Object.keys(params)
      .sort()
      .reduce((acc, key) => {
        if (params[key] !== undefined && params[key] !== null) {
          acc[key] = params[key];
        }
        return acc;
      }, {});
    
    return `${url}:${JSON.stringify(sortedParams)}`;
  }

  /**
   * Get item from cache
   */
  async get(key) {
    // First check memory cache
    if (this.memoryCache.has(key)) {
      const cached = this.memoryCache.get(key);
      if (this.isValid(cached)) {
        this.stats.hits++;
        // Move to end (LRU)
        this.memoryCache.delete(key);
        this.memoryCache.set(key, cached);
        return cached.data;
      } else {
        // Remove expired entry
        this.memoryCache.delete(key);
      }
    }

    // Then check localStorage
    try {
      const stored = localStorage.getItem(this.localStorageKey);
      if (stored) {
        const cache = JSON.parse(stored);
        if (cache[key] && this.isValid(cache[key])) {
          this.stats.hits++;
          // Add to memory cache for faster access
          this.addToMemoryCache(key, cache[key]);
          return cache[key].data;
        }
      }
    } catch (error) {
      console.error('Error reading from localStorage cache:', error);
    }

    this.stats.misses++;
    return null;
  }

  /**
   * Set item in cache
   */
  set(key, data, ttl = this.defaultTTL) {
    const cacheItem = {
      data,
      timestamp: Date.now(),
      ttl,
      expires: Date.now() + ttl
    };

    this.stats.sets++;

    // Add to memory cache
    this.addToMemoryCache(key, cacheItem);

    // Add to localStorage
    try {
      const stored = localStorage.getItem(this.localStorageKey);
      const cache = stored ? JSON.parse(stored) : {};
      cache[key] = cacheItem;
      
      // Check localStorage size and clean if needed
      const cacheString = JSON.stringify(cache);
      if (cacheString.length > 5 * 1024 * 1024) { // 5MB limit
        this.cleanLocalStorage(cache);
      } else {
        localStorage.setItem(this.localStorageKey, cacheString);
      }
    } catch (error) {
      console.error('Error writing to localStorage cache:', error);
      // If localStorage is full, clear old entries
      if (error.name === 'QuotaExceededError') {
        this.clearLocalStorage();
      }
    }
  }

  /**
   * Check if item exists in cache
   */
  has(key) {
    if (this.memoryCache.has(key)) {
      const cached = this.memoryCache.get(key);
      if (this.isValid(cached)) {
        return true;
      }
    }

    try {
      const stored = localStorage.getItem(this.localStorageKey);
      if (stored) {
        const cache = JSON.parse(stored);
        return cache[key] && this.isValid(cache[key]);
      }
    } catch (error) {
      console.error('Error checking localStorage cache:', error);
    }

    return false;
  }

  /**
   * Clear all cache
   */
  clear() {
    this.memoryCache.clear();
    this.clearLocalStorage();
    this.resetStats();
  }

  /**
   * Clear expired entries
   */
  clearExpired() {
    const now = Date.now();
    
    // Clear from memory cache
    for (const [key, value] of this.memoryCache.entries()) {
      if (!this.isValid(value)) {
        this.memoryCache.delete(key);
      }
    }

    // Clear from localStorage
    try {
      const stored = localStorage.getItem(this.localStorageKey);
      if (stored) {
        const cache = JSON.parse(stored);
        let hasChanges = false;
        
        for (const key in cache) {
          if (!this.isValid(cache[key])) {
            delete cache[key];
            hasChanges = true;
          }
        }
        
        if (hasChanges) {
          localStorage.setItem(this.localStorageKey, JSON.stringify(cache));
        }
      }
    } catch (error) {
      console.error('Error clearing expired entries from localStorage:', error);
    }
  }

  /**
   * Clear cache by pattern
   */
  clearByPattern(pattern) {
    // Clear from memory cache
    for (const key of this.memoryCache.keys()) {
      if (key.includes(pattern)) {
        this.memoryCache.delete(key);
      }
    }

    // Clear from localStorage
    try {
      const stored = localStorage.getItem(this.localStorageKey);
      if (stored) {
        const cache = JSON.parse(stored);
        let hasChanges = false;
        
        for (const key in cache) {
          if (key.includes(pattern)) {
            delete cache[key];
            hasChanges = true;
          }
        }
        
        if (hasChanges) {
          localStorage.setItem(this.localStorageKey, JSON.stringify(cache));
        }
      }
    } catch (error) {
      console.error('Error clearing cache by pattern:', error);
    }
  }

  /**
   * Get cache statistics
   */
  getStats() {
    const hitRate = this.stats.hits + this.stats.misses > 0
      ? (this.stats.hits / (this.stats.hits + this.stats.misses) * 100).toFixed(2)
      : 0;
    
    return {
      ...this.stats,
      hitRate: `${hitRate}%`,
      memoryCacheSize: this.memoryCache.size,
      localStorageSize: this.getLocalStorageSize()
    };
  }

  /**
   * Reset statistics
   */
  resetStats() {
    this.stats = {
      hits: 0,
      misses: 0,
      sets: 0
    };
  }

  // Private methods

  /**
   * Check if cache item is still valid
   */
  isValid(cacheItem) {
    if (!cacheItem) return false;
    return Date.now() < cacheItem.expires;
  }

  /**
   * Add item to memory cache with LRU eviction
   */
  addToMemoryCache(key, cacheItem) {
    // Remove oldest entry if cache is full
    if (this.memoryCache.size >= this.maxMemoryCacheSize) {
      const firstKey = this.memoryCache.keys().next().value;
      this.memoryCache.delete(firstKey);
    }
    
    this.memoryCache.set(key, cacheItem);
  }

  /**
   * Clear localStorage cache
   */
  clearLocalStorage() {
    try {
      localStorage.removeItem(this.localStorageKey);
    } catch (error) {
      console.error('Error clearing localStorage cache:', error);
    }
  }

  /**
   * Clean localStorage by removing oldest entries
   */
  cleanLocalStorage(cache) {
    const entries = Object.entries(cache);
    // Sort by expiration time
    entries.sort((a, b) => a[1].expires - b[1].expires);
    
    // Remove oldest 25% of entries
    const toRemove = Math.floor(entries.length * 0.25);
    for (let i = 0; i < toRemove; i++) {
      delete cache[entries[i][0]];
    }
    
    try {
      localStorage.setItem(this.localStorageKey, JSON.stringify(cache));
    } catch (error) {
      console.error('Error cleaning localStorage:', error);
    }
  }

  /**
   * Get localStorage cache size in bytes
   */
  getLocalStorageSize() {
    try {
      const stored = localStorage.getItem(this.localStorageKey);
      return stored ? new Blob([stored]).size : 0;
    } catch (error) {
      return 0;
    }
  }
}

// Create singleton instance
const cacheService = new CacheService();

export default cacheService;